import { useEffect, useRef } from "react";

// Dot-grid mountain engine (ported from the supplied reference code),
// re-paletted for Nagel: grey mountain + retro-green vegetation,
// dense orange sun rising from BEHIND the ridge, with post-rise light rays.

const vt = (e) => { const t = 43758.5453 * Math.sin(e); return t - Math.floor(t); };
const gt = (e, t, n) => { let r = n; for (const a of t) { const d = Math.abs(e - a.c) / a.w; if (d < 1) r = Math.max(r, a.h * (1 - d)); } return r; };

const BACK_PEAKS = [{ c: 0.6, h: 0.28, w: 0.32 }, { c: 0.3, h: 0.12, w: 0.28 }];
const MID_PEAKS = [{ c: 0.92, h: 0.42, w: 0.2 }, { c: 0.7, h: 0.26, w: 0.16 }, { c: 0.5, h: 0.14, w: 0.2 }, { c: 0.15, h: 0.05, w: 0.22 }];
const FRONT_PEAKS = [{ c: 0.99, h: 0.47, w: 0.28 }, { c: 0.8, h: 0.42, w: 0.14 }, { c: 0.63, h: 0.34, w: 0.16 }, { c: 0.45, h: 0.22, w: 0.2 }, { c: 0.25, h: 0.08, w: 0.18 }];

const PAL = {
  back: ["#AEB4AB", "#9FA69C", "#90988D"],
  mid: ["#7C847B", "#68716A", "#565F58"],
  front: ["#6E756C", "#5C645B", "#4C544D", "#3E463F"],
  hot: "#E65100",
  flash: "#8A9A87",
  sky: "rgba(110,120,108,0.35)",
};
const GREENS = ["#3E7C33", "#2E6930", "#4C8A3F", "#356F2E"];
const SUN_CORE = ["#FF9A5C", "#F07A2E"];
const SUN_MID = ["#F05A24", "#E65100"];
const SUN_EDGE = ["#E65100", "#D84300"];
const SUN_HOT = "#FF6D2D";
const SUN_FLASH = "#FFE3C2";

const isBirdCell = (x, y, flap) => {
  const cx = 15, cy = 9;
  const dx = Math.abs(x - cx);
  if ((x - cx) ** 2 / 22 + (y - cy) ** 2 / 4.5 <= 1) return true;
  if ((x - (cx + 4.5)) ** 2 / 2.4 + (y - (cy - 2)) ** 2 / 2 <= 1) return true;
  if (dx > 2 && dx <= 13) {
    const wy = cy - dx * (0.55 + flap * 0.38);
    const th = 2.2 - dx * 0.12;
    if (Math.abs(y - wy) < th) return true;
  }
  if ((x - (cx - 4.5)) ** 2 / 7 + (y - (cy + 0.5)) ** 2 / 1.4 <= 1) return true;
  return false;
};

export const PixelWorld = () => {
  const ref = useRef(null);

  useEffect(() => {
    const canvas = ref.current;
    const wrap = canvas.parentElement;
    const ctx = canvas.getContext("2d");
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const density = 7;
    let raf, running = true, visible = true;
    let u = 0, f = 0, h = density;
    let mtn = [], sky = [], sun = [], rays = [], birds = [];
    let y = 40 * Math.random();
    let last = performance.now();
    const startT = performance.now();
    const ripples = [];
    let lmx = -9999, lmy = -9999;
    const M = { x: -9999, y: -9999 };
    let sunCx = 0, sunCy = 0, sunR = 0;

    const build = () => {
      const mobile = window.innerWidth < 768;
      h = mobile ? density + 1 : density;
      // composition matched to the client-supplied reference:
      // long ridge sweeping low-left → dominant peak far right; sun in the valley
      const tShift = 0;
      const tScale = 1;
      const hgtScale = mobile ? 0.7 : 1;

      const rect = wrap.getBoundingClientRect();
      u = Math.max(1, rect.width);
      f = Math.max(1, rect.height);
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.round(u * dpr);
      canvas.height = Math.round(f * dpr);
      canvas.style.width = `${u}px`;
      canvas.style.height = `${f}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      sunCx = u * (mobile ? 0.82 : 0.72);
      sunCy = f * (mobile ? 0.66 : 0.56);
      sunR = mobile ? Math.min(u, f) * 0.26 : Math.min(u * 0.10, f * 0.20);

      const ridgeAt = (t) => {
        const ts = Math.max(0, (t - tShift) / tScale);
        const n = 1 - (0.015 + gt(ts, BACK_PEAKS, 0) * hgtScale) * (0.96 + 0.08 * vt(2.17 * Math.round(t * 200)));
        const rm = 1 - (0.025 + gt(ts, MID_PEAKS, 0) * hgtScale) * (0.95 + 0.1 * vt(3.71 * Math.round(t * 200)));
        const fr = 1 - (0.04 + gt(ts, FRONT_PEAKS, 0) * hgtScale) * (0.96 + 0.08 * vt(5.13 * Math.round(t * 200)));
        return { n, rm, fr, min: Math.min(n, rm, fr) };
      };

      const cols = Math.ceil(u / h), rows = Math.ceil(f / h), v = 1 / rows;
      mtn = []; sky = []; sun = []; rays = [];
      for (let e = 0; e < cols; e++) {
        const t = e / cols;
        const ts = Math.max(0, (t - tShift) / tScale);
        const n = 1 - (0.015 + gt(ts, BACK_PEAKS, 0) * hgtScale) * (0.96 + 0.08 * vt(2.17 * e));
        const rm = 1 - (0.025 + gt(ts, MID_PEAKS, 0) * hgtScale) * (0.95 + 0.1 * vt(3.71 * e));
        const fr = 1 - (0.04 + gt(ts, FRONT_PEAKS, 0) * hgtScale) * (0.96 + 0.08 * vt(5.13 * e));
        const ridgeMin = Math.min(n, rm, fr);
        for (let i = 0; i < rows; i++) {
          const l = i / rows;
          const s = vt(12.9898 * e + 78.233 * i);
          const gx = e * h + h / 2, gy = i * h + h / 2;
          if (l < ridgeMin) {
            const sdx = gx - sunCx, sdy = gy - sunCy;
            const sd = Math.sqrt(sdx * sdx + sdy * sdy);
            if (sd < sunR) {
              const rr = sd / sunR;
              if (rr > 0.85 && s > 0.86) continue;
              const col = rr < 0.45 ? SUN_CORE[(s * 2) | 0] : rr < 0.75 ? SUN_MID[(s * 2) | 0] : SUN_EDGE[(s * 2) | 0];
              sun.push({ x: gx, y: gy, seed: s, phase: s * Math.PI * 2, speed: 0.8 + 1.6 * vt(7.7 * e + 2.9 * i), solid: false, r: h * (0.3 + 0.16 * (1 - rr)), base: 0.95 - 0.45 * rr, color: col, sun: true, ridge: ridgeMin });
            } else if (s > 0.985) {
              sky.push({ x: e * h, y: i * h, seed: s });
            }
            continue;
          }
          let d, alpha, g2, solid = false, layer;
          if (l >= fr) {
            layer = "front";
            const dn = l - fr;
            const rr = Math.min(1, 0.45 + 1.7 * dn + 0.35 * Math.pow(t, 1.4));
            if (t > 0.76 && rr > 0.88) {
              solid = true;
              const te = Math.floor(e / 3), ti = Math.floor(i / 3);
              if (vt(91.7 * te + 41.3 * ti) < 0.06) continue;
              if (s < 0.045) continue;
              d = PAL.front[3]; alpha = 1; g2 = 0.55 * h;
            } else {
              if (s > 0.94) continue;
              d = PAL.front[Math.min(3, Math.floor(4 * rr))];
              alpha = 0.85 + 0.15 * rr;
              g2 = h * (0.3 + 0.18 * rr);
            }
          } else if (l >= rm) {
            layer = "mid";
            const dn = l - rm;
            const near = dn < 2 * v;
            if (!near && s > 0.9) continue;
            d = PAL.mid[Math.min(2, Math.floor(5 * dn))];
            alpha = (near ? 0.98 : 0.8 + 0.5 * dn) * (0.6 + 0.4 * Math.min(1, 2.6 * t));
            g2 = h * (0.3 + 0.22 * dn);
          } else {
            layer = "back";
            const dn = l - n;
            const near = dn < 2 * v;
            if (!near && s > 0.82) continue;
            d = PAL.back[Math.min(2, Math.floor(5 * dn))];
            alpha = near ? 0.92 : 0.65 + 0.4 * dn;
            g2 = 0.3 * h;
          }
          if (!solid && layer !== "back") {
            const gn = Math.sin(gx * 0.021 + gy * 0.013) * Math.sin(gx * 0.009 - gy * 0.017 + 1.7);
            if (gn > 0.52 && t > 0.42) d = GREENS[(s * 4) | 0];
          }
          mtn.push({ x: gx, y: gy, seed: s, phase: s * Math.PI * 2, speed: 0.8 + 1.6 * vt(7.7 * e + 2.9 * i), solid, r: g2, base: Math.min(1, alpha), color: d, sun: false, ridge: 2 });
        }
      }

      // soft pixel light-rays radiating from the sun (revealed after sunrise)
      const RAY_N = 70;
      for (let k = 0; k < RAY_N; k++) {
        const th = Math.PI * (0.08 + 0.84 * (k / RAY_N));
        const cth = Math.cos(th), sth = Math.sin(th);
        for (let j = 0; j < 7; j++) {
          const dist = sunR * (1.08 + j * 0.22);
          const rx = sunCx + cth * dist;
          const ry = sunCy - sth * dist;
          if (ry < -10 || rx < -10 || rx > u + 10) break;
          const ridge = ridgeAt(Math.min(1, Math.max(0, rx / u))).min;
          if (ry / f > ridge - 0.01) break; // stop rays at the ridgeline
          rays.push({ x: rx, y: ry, base: 0.20 * (1 - j / 8), ph: vt(k * 3.3 + j * 7.1) * Math.PI * 2, tw: 0.6 + vt(k + j * 13.7) });
        }
      }

      const BN = mobile ? 3 : 5;
      birds = Array.from({ length: BN }, (_, i) => ({
        p: Math.random() * 1.3 - 0.15,
        speed: 0.020 + Math.random() * 0.035,
        yBase: f * (0.08 + Math.random() * 0.24),
        scale: [4.6, 3.6, 2.8, 4.2, 3.0][i % 5] * (mobile ? 0.7 : 1),
        ph: Math.random() * Math.PI * 2,
        amp: 12 + Math.random() * 20,
      }));
    };

    const drawDot = (d, riseOff) => {
      const dyOff = d.sun ? riseOff : 0;
      let edgeK = 1;
      if (d.sun) {
        const l = (d.y + dyOff) / f;
        const soft = (d.ridge - l) / 0.03;
        if (soft <= 0) return; // fully behind the mountain
        edgeK = Math.min(1, soft);
      }
      const pulse = 0.86 + 0.14 * Math.sin(y * d.speed + d.phase);
      const fs = vt(517.7 * d.seed + Math.floor(1.5 * y));
      const flash = fs > 0.972 ? (fs - 0.972) / 0.028 : 0;
      const dd = d.x / u - sweepV;
      const sweepGlow = Math.exp(-dd * dd / 0.0022);
      let rmag = 0, vx = 0, vy = 0;
      for (const rp of ripples) {
        const nx = d.x - rp.x, ny = (d.y + dyOff) - rp.y;
        const a = Math.sqrt(nx * nx + ny * ny) || 1;
        const o = a - rp.r;
        const inf = Math.exp(-o * o / 900) * rp.life * rp.strength;
        if (inf > 0.02) { rmag += inf; vx += (nx / a) * inf * 7; vy += (ny / a) * inf * 7; }
      }
      if (rmag > 1.4) rmag = 1.4;
      const mx = d.x - M.x, my = (d.y + dyOff) - M.y;
      const dist2 = mx * mx + my * my;
      let S = 0, E = 0;
      if (dist2 < 15625) {
        S = 1 - dist2 / 15625;
        S *= S;
        const dist = Math.sqrt(dist2) || 1;
        const ring = dist - 74;
        E = Math.exp(-ring * ring / 1350);
        vx += (mx / dist) * S * 9;
        vy += (my / dist) * S * 9;
      }
      let A = (d.base * pulse + 0.5 * flash + 0.12 * sweepGlow + 0.3 * E + 0.2 * rmag) * edgeK;
      if (A > 1) A = 1;
      ctx.globalAlpha = A;
      let col = d.color;
      if (E > 0.4 || rmag > 0.7) col = d.sun ? SUN_HOT : PAL.hot;
      else if (flash > 0.5) col = d.sun ? SUN_FLASH : PAL.flash;
      ctx.fillStyle = col;
      const P = 1 + 0.28 * S + 0.55 * E + 0.4 * rmag + 0.45 * flash;
      const jx = d.x + vx, jy = d.y + dyOff + vy;
      if (d.solid) {
        const sz = 1.04 * h * (1 + 0.2 * E);
        ctx.fillRect(jx - sz / 2, jy - sz / 2, sz, sz);
      } else {
        ctx.beginPath();
        ctx.arc(jx, jy, d.r * P, 0, 6.2832);
        ctx.fill();
      }
    };

    let sweepV = 0;
    const drawBirds = (t) => {
      ctx.fillStyle = "#1E2022";
      for (const b of birds) {
        b.p += b.speed * 0.032;
        if (b.p > 1.2) {
          b.p = -0.2;
          b.yBase = f * (0.06 + Math.random() * 0.26);
        }
        const bx = b.p * u;
        const by = b.yBase - Math.sin(b.p * Math.PI) * f * 0.07 + Math.sin(b.p * 5 + b.ph) * b.amp * 0.5;
        const flap = Math.sin(t * 0.008 + b.ph);
        const cs = b.scale;
        for (let gx = 0; gx < 30; gx++) {
          for (let gy = 0; gy < 16; gy++) {
            if (!isBirdCell(gx, gy, flap)) continue;
            const hh = vt(gx * 3.1 + gy * 7.7 + b.ph);
            if (hh < 0.05) continue;
            ctx.globalAlpha = 0.6 + hh * 0.4;
            ctx.fillRect(bx + (gx - 15) * cs, by + (gy - 9) * cs, cs * 0.72, cs * 0.72);
          }
        }
      }
    };

    const draw = (t) => {
      ctx.clearRect(0, 0, u, f);
      // sunrise progress: 0..1 over ~15s, then rays fade in 1..1.3
      const el = (t - startT) / 15000;
      const k = Math.min(1, Math.max(0, el));
      const ease = 1 - Math.pow(1 - k, 3);
      const riseOff = (1 - ease) * f * 0.30 + Math.sin(y * 0.9) * 6;
      let post = Math.min(1, Math.max(0, (el - 1) * 2.4));
      post = post * post * (3 - 2 * post);

      ctx.fillStyle = PAL.sky;
      for (const e of sky) {
        ctx.globalAlpha = 0.18 + 0.32 * (0.5 + 0.5 * Math.sin(1.4 * y + 14 * e.seed));
        ctx.fillRect(e.x, e.y, 2, 2);
      }

      // atmospheric glow once the sun has risen
      if (post > 0.01) {
        const g = ctx.createRadialGradient(sunCx, sunCy, sunR * 0.3, sunCx, sunCy, sunR * 2.4);
        g.addColorStop(0, `rgba(240,110,50,${0.30 * post})`);
        g.addColorStop(0.55, `rgba(240,120,60,${0.12 * post})`);
        g.addColorStop(1, "rgba(240,120,60,0)");
        ctx.globalAlpha = 1;
        ctx.fillStyle = g;
        ctx.fillRect(0, 0, u, f);
        // pixel rays
        ctx.fillStyle = "#F07A2E";
        for (const r of rays) {
          const flick = 0.55 + 0.45 * Math.sin(y * 1.1 * r.tw + r.ph);
          ctx.globalAlpha = r.base * post * flick;
          const sway = Math.sin(y * 0.5 + r.ph) * 3;
          ctx.fillRect(r.x + sway, r.y, 2.4, 2.4);
        }
      }

      sweepV = (0.05 * y) % 1.5 - 0.25;
      for (const d of sun) drawDot(d, riseOff);
      for (const d of mtn) drawDot(d, riseOff);
      drawBirds(t);
      ctx.globalAlpha = 1;
    };

    const frame = (now) => {
      if (!running) return;
      raf = requestAnimationFrame(frame);
      if (!visible || now - last < 30) return;
      last = now;
      y += 0.035;
      for (let i = ripples.length - 1; i >= 0; i--) {
        const r = ripples[i];
        r.r += r.v;
        r.life -= 0.018 + 0.0011 * r.v;
        if (r.life <= 0) ripples.splice(i, 1);
      }
      draw(now);
    };

    const addRipple = (x2, y2, strength, v) => {
      ripples.push({ x: x2, y: y2, r: 6, v, life: 1, strength });
      if (ripples.length > 6) ripples.shift();
    };

    const onMove = (ev) => {
      const rect = canvas.getBoundingClientRect();
      const px = ev.clientX - rect.left, py = ev.clientY - rect.top;
      if (px >= -60 && px <= rect.width + 60 && py >= -60 && py <= rect.height + 60) {
        M.x = px; M.y = py;
        const dx = px - lmx, dy2 = py - lmy;
        if (dx * dx + dy2 * dy2 > 3600) { lmx = px; lmy = py; addRipple(px, py, 0.55, 3.4); }
      } else {
        M.x = -9999; M.y = -9999;
      }
    };
    const onDown = (ev) => {
      const rect = canvas.getBoundingClientRect();
      const px = ev.clientX - rect.left, py = ev.clientY - rect.top;
      if (px >= 0 && px <= rect.width && py >= 0 && py <= rect.height) addRipple(px, py, 1.7, 6.5);
    };

    const io = new IntersectionObserver((entries) => { visible = entries[0].isIntersecting; }, { threshold: 0 });
    io.observe(wrap);

    build();
    if (reduced) {
      draw(startT + 20000); // render post-rise static frame
    } else {
      raf = requestAnimationFrame(frame);
      window.addEventListener("pointermove", onMove, { passive: true });
      window.addEventListener("pointerdown", onDown, { passive: true });
    }
    const onResize = () => { build(); if (reduced) draw(startT + 20000); };
    window.addEventListener("resize", onResize);

    return () => {
      running = false;
      cancelAnimationFrame(raf);
      io.disconnect();
      window.removeEventListener("resize", onResize);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerdown", onDown);
    };
  }, []);

  return <canvas ref={ref} data-testid="pixel-sun-canvas" className="absolute inset-0 pointer-events-none" aria-hidden="true" />;
};
